import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "abc_student_token";

const StudentAuthContext = createContext(null);

export const useStudentAuth = () => {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error("useStudentAuth must be used within StudentAuthProvider");
  return ctx;
};

export const StudentAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [checked, setChecked] = useState(false);

  const authHeaders = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setUser(null);
      setReservations([]);
      setChecked(true);
      return null;
    }
    try {
      const { data } = await axios.get(`${API}/api/students/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(data.user);
      setReservations(data.reservations || []);
      setChecked(true);
      return data.user;
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      setReservations([]);
      setChecked(true);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (identifier, password) => {
    const { data } = await axios.post(`${API}/api/students/login`, { identifier, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const register = async ({ email, username, password, name }) => {
    const { data } = await axios.post(`${API}/api/students/register`, { email, username, password, name });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setReservations([]);
  };

  const updateProfile = async (patch) => {
    const { data } = await axios.patch(`${API}/api/students/profile`, patch, { headers: authHeaders() });
    setUser(data);
    return data;
  };

  return (
    <StudentAuthContext.Provider value={{ user, reservations, checked, login, register, logout, refresh, updateProfile, authHeaders }}>
      {children}
    </StudentAuthContext.Provider>
  );
};

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
